import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';

import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/search/sp-search.js';
import type { TaskDefinition } from '../../../api/types';
import { taskService } from '../../../services/task-service';

interface TreeNode {
  children: Map<string, TreeNode>;
  definitions: TaskDefinition[];
}

@localized()
export class SbTaskTree extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
    }
    .task-search-row {
      position: sticky;
      top: 0;
      z-index: 2;
      display: flex;
      align-items: center;
      padding-bottom: 10px;
      background: var(--color-sidebar-surface);
    }
    sp-search {
      flex: 1;
      min-width: 0;
      width: 100%;
      --mod-search-border-color: var(--color-app-border);
      --mod-search-border-color-hover: var(--spectrum-gray-500);
    }
    .task-tree-panel {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }
    .task-list {
      flex: 1;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      overflow-x: hidden;
      overflow-y: auto;
      min-height: 0;
      padding-bottom: 8px;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .task-list::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
    .task-category {
      margin: 0 0 2px;
      min-width: 0;
    }
    .task-category summary {
      display: flex;
      align-items: center;
      gap: 7px;
      box-sizing: border-box;
      max-width: 100%;
      min-width: 0;
      min-height: 28px;
      padding: 5px 2px 5px calc((var(--depth, 0) * 16px) + 2px);
      color: var(--spectrum-gray-900);
      cursor: pointer;
      font-size: 12px;
      font-weight: 650;
      line-height: 1.2;
      list-style: none;
    }
    .task-category summary::-webkit-details-marker {
      display: none;
    }
    .task-category summary::before {
      width: 12px;
      color: var(--spectrum-gray-600);
      content: '›';
      font-size: 15px;
      line-height: 1;
      transform: rotate(90deg);
      transition: transform 0.12s ease;
    }
    .task-category:not([open]) summary::before {
      transform: rotate(0deg);
    }
    .category-count {
      margin-left: auto;
      color: var(--spectrum-gray-500);
      font-size: 11px;
      font-weight: 500;
    }
    .category-body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
      margin: 1px 0 8px;
    }
    .task-row {
      display: flex;
      align-items: center;
      gap: 10px;
      box-sizing: border-box;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      min-height: 42px;
      padding: 5px 7px 5px calc((var(--depth, 0) * 16px) + 7px);
      border: 1px solid transparent;
      border-radius: 8px;
      background: transparent;
      color: inherit;
      font: inherit;
      text-align: left;
      cursor: grab;
      transition:
        background 0.12s ease,
        border-color 0.12s ease;
    }
    .task-row:hover,
    .task-row:focus-visible {
      border-color: var(--color-app-border);
      background: var(--color-sidebar-surface-hover);
      outline: none;
    }
    .task-row:hover .task-icon,
    .task-row:focus-visible .task-icon {
      color: var(--color-accent);
    }
    .task-row:active {
      cursor: grabbing;
    }
    .task-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      color: var(--color-app-text-secondary);
      flex-shrink: 0;
      align-self: center;
      margin: 0;
    }
    .task-icon svg {
      width: 17px;
      height: 17px;
      color: currentColor;
      opacity: 0.86;
    }
    .task-copy {
      display: flex;
      flex: 1;
      min-width: 0;
      flex-direction: column;
      gap: 2px;
    }
    .task-name {
      overflow: hidden;
      color: var(--spectrum-gray-900);
      font-size: 13px;
      font-weight: 600;
      line-height: 1.25;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .task-description {
      display: -webkit-box;
      overflow: hidden;
      color: var(--spectrum-gray-600);
      font-size: 11px;
      line-height: 1.25;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
    }
    .empty-state {
      margin-top: 24px;
      padding: 18px 12px;
      border: 1px dashed var(--color-app-border);
      border-radius: 8px;
      background: var(--color-sidebar-bg);
      color: var(--spectrum-gray-600);
      font-size: 12px;
      line-height: 1.45;
      text-align: center;
    }
  `;

  static properties = {
    categories: { type: Array },
    definitions: { type: Array },
    filter: { state: true },
  } as const;

  declare categories: string[];
  declare definitions: TaskDefinition[];
  declare filter: string;
  private icons = new Map<string, string>();

  private getShortcutLabel() {
    const platform = navigator.platform?.toLowerCase() ?? '';
    return platform.includes('mac') ? '⌘K' : 'Ctrl+K';
  }

  async focusSearch() {
    const search = this.renderRoot.querySelector('sp-search') as
      | (HTMLElement & {
          focus?: () => void;
          updateComplete?: Promise<unknown>;
          shadowRoot?: ShadowRoot | null;
        })
      | null;
    if (!search) return;
    await search.updateComplete;
    search.focus?.();
    const input = search.shadowRoot?.querySelector('input');
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }

  private onItemDblClick(e: MouseEvent) {
    const type = (e.currentTarget as HTMLElement).dataset.type;
    if (type) {
      this.dispatchAddTask(type);
    }
  }

  private onItemKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Enter') return;
    const type = (e.currentTarget as HTMLElement).dataset.type;
    if (!type) return;
    e.preventDefault();
    this.dispatchAddTask(type);
  }

  private dispatchAddTask(type: string) {
    this.dispatchEvent(
      new CustomEvent('add-task', {
        detail: { type },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onItemDragStart(e: DragEvent) {
    const type = (e.currentTarget as HTMLElement).dataset.type;
    const dataTransfer = e.dataTransfer;
    if (type && dataTransfer) {
      dataTransfer.setData('text/plain', JSON.stringify({ type }));
      dataTransfer.effectAllowed = 'copy';
    } else {
      e.preventDefault();
    }
  }

  private onFilterInput(e: Event) {
    this.filter = (e.target as unknown as { value: string }).value;
  }

  private async loadIcons() {
    if (!this.definitions || this.definitions.length === 0) return;

    const iconPromises = this.definitions.map(async (def) => {
      if (!this.icons.has(def.name)) {
        try {
          const icon = await taskService.getIcon(def.name);
          this.icons.set(def.name, icon);
        } catch (error) {
          console.warn(`Failed to load icon for task ${def.name}:`, error);
        }
      }
    });

    await Promise.all(iconPromises);
    this.requestUpdate();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('definitions')) {
      this.loadIcons();
    }
  }

  private buildTree() {
    const filter = (this.filter || '').toLowerCase();
    const matches = (def: TaskDefinition) =>
      !filter ||
      def.name.toLowerCase().includes(filter) ||
      def.category.toLowerCase().includes(filter) ||
      def.description.toLowerCase().includes(filter);

    const root: TreeNode = { children: new Map(), definitions: [] };
    for (const cat of this.categories) {
      let node = root;
      for (const part of cat.split('/')) {
        let next = node.children.get(part);
        if (!next) {
          next = { children: new Map(), definitions: [] };
          node.children.set(part, next);
        }
        node = next;
      }
    }
    for (const def of this.definitions) {
      if (!matches(def)) continue;
      let node = root;
      for (const part of def.category.split('/')) {
        let next = node.children.get(part);
        if (!next) {
          next = { children: new Map(), definitions: [] };
          node.children.set(part, next);
        }
        node = next;
      }
      node.definitions.push(def);
    }

    const prune = (node: TreeNode): boolean => {
      for (const [name, child] of node.children) {
        if (!prune(child)) node.children.delete(name);
      }
      return node.definitions.length > 0 || node.children.size > 0;
    };
    prune(root);
    return root;
  }

  private formatLabel(value: string) {
    const replacements = new Map([
      ['ai', 'AI'],
      ['api', 'API'],
      ['datev', 'DATEV'],
      ['http', 'HTTP'],
      ['imap', 'IMAP'],
      ['json', 'JSON'],
      ['llm', 'LLM'],
      ['mcp', 'MCP'],
      ['oauth2', 'OAuth2'],
      ['pdf', 'PDF'],
      ['url', 'URL'],
      ['xml', 'XML'],
    ]);
    return value
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => {
        const lower = part.toLowerCase();
        return (
          replacements.get(lower) ??
          `${part.charAt(0).toUpperCase()}${part.slice(1)}`
        );
      })
      .join(' ');
  }

  private countDefinitions(node: TreeNode): number {
    let count = node.definitions.length;
    for (const child of node.children.values()) {
      count += this.countDefinitions(child);
    }
    return count;
  }

  private renderTree(node: TreeNode, path: string[] = []): unknown[] {
    const children = repeat(
      Array.from(node.children.entries()).sort(([a], [b]) =>
        a.localeCompare(b)
      ),
      ([name]) => [...path, name].join('/'),
      ([name, child]) => html`
        <details class="task-category" style=${`--depth: ${path.length};`} open>
          <summary>
            <span>${this.formatLabel(name)}</span>
            <span class="category-count">${this.countDefinitions(child)}</span>
          </summary>
          <div class="category-body">
            ${this.renderTree(child, [...path, name])}
          </div>
        </details>
      `
    );
    const definitions = repeat(
      node.definitions
        .flatMap((d) => ({ d, type: d.name }))
        .sort((a, b) => a.d.name.localeCompare(b.d.name)),
      ({ type }) => `${path.join('/')}:${type}`,
      ({ d, type }) => {
        const icon = this.icons.get(d.name);
        return html`
          <div
            class="task-row"
            style=${`--depth: ${path.length};`}
            role="button"
            tabindex="0"
            title=${`${this.formatLabel(d.name)}: ${d.description}`}
            data-type=${type}
            draggable="true"
            @dragstart=${this.onItemDragStart}
            @dblclick=${this.onItemDblClick}
            @keydown=${this.onItemKeyDown}
          >
            ${
              icon
                ? html`<div class="task-icon">${unsafeSVG(icon)}</div>`
                : html`<div class="task-icon" aria-hidden="true"></div>`
            }
            <div class="task-copy">
              <span class="task-name">${this.formatLabel(d.name)}</span>
              <span class="task-description">${d.description}</span>
            </div>
          </div>
        `;
      }
    );
    return [children, definitions];
  }

  render() {
    const tree: any = this.buildTree();

    return html`
      <div class="task-tree-panel">
        <div class="task-search-row">
          <sp-search
            data-testid="task-tree-search"
            aria-label=${msg('Search in tasks')}
            placeholder=${msg(str`Search tasks (${this.getShortcutLabel()})`)}
            .value=${this.filter}
            @input=${this.onFilterInput}
          ></sp-search>
        </div>
        <div class="task-list" data-testid="task-tree">
          ${
            this.countDefinitions(tree) > 0
              ? this.renderTree(tree)
              : html`<div class="empty-state">
                ${msg('No tasks match your search. Try a task name, category, or description.')}
              </div>`
          }
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sb-task-tree': SbTaskTree;
  }
}

customElements.define('sb-task-tree', SbTaskTree);
